import React, { useEffect } from 'react';
import { applyLang, toggleLang, getCurrentLang } from '../utils/i18n';

// Compteurs en direct : on récupère le total des différentes entités publiques
// depuis l'API et on met à jour les attributs data-target des éléments
// .counter b[data-target] juste après le rendu HTML.
const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5056/api').replace(/\/$/, '');

async function fetchCount(path) {
  try {
    const r = await fetch(`${API_BASE}${path}?limit=1`);
    if (!r.ok) return null;
    const j = await r.json();
    return Number.isFinite(j?.total) ? j.total : null;
  } catch { return null; }
}

const BODY_HTML = `
<!-- ============================== HERO ============================== -->
<header class="hero" data-screen-label="01 Hero">
  <div class="hero-bg"></div>
  <div class="hero-grain"></div>
  <div class="hero-vignette"></div>
  <div class="hero-photo-tag">Drone · Cambérène · 06h47 — Photo by ____</div>

  <div class="hero-counters">
    <div><b>14&nbsp;km</b><span data-i18n="hero_counter1">De Dakar centre</span></div>
    <div><b>1884</b><span data-i18n="hero_counter2">Fondation du village</span></div>
    <div><b>32°C</b><span data-i18n="hero_counter3">Plage · Vent O 14 km/h</span></div>
  </div>

  <div class="hero-content">
    <div class="eyebrow" style="color:rgba(250,246,238,.75)" data-i18n="hero_eyebrow">Cambérène · Côte Atlantique · Sénégal</div>
    <h1 data-i18n="hero_h1">
      Entre spiritualité,<br />
      culture <span class="ital">&amp; modernité.</span>
    </h1>
    <div class="hero-meta">
      <p class="lede" data-i18n="hero_lede">
        Village layène posé sur la côte nord de Dakar, Cambérène raconte une mémoire
        ancienne et un présent vibrant — pirogues, terrains de sable, voix de la mer.
      </p>
      <div class="hero-actions">
        <a class="btn btn-primary" href="#discover">
          <span data-i18n="hero_btn_primary">Découvrir le village</span>
          <span class="arrow">↗</span>
        </a>
        <a class="btn btn-ghost" href="/histoire">
          <span data-i18n="hero_btn_ghost">Explorer la culture</span>
          <span class="arrow">→</span>
        </a>
      </div>
    </div>
  </div>

  <div class="scroll-cue">
    <span data-i18n="hero_scroll">Faire défiler</span>
    <span class="line"></span>
  </div>
</header>

<!-- ============================== MARQUEE ============================== -->
<div class="marquee" aria-hidden="true">
  <div class="marquee-track">
    <span>Layène <i class="dot"></i> Pirogues <i class="dot"></i> Tassou Yalla <i class="dot"></i> ASC Cambérène <i class="dot"></i> Plage Nord <i class="dot"></i> Diakhle <i class="dot"></i> Yoff–Cambérène <i class="dot"></i></span>
    <span>Layène <i class="dot"></i> Pirogues <i class="dot"></i> Tassou Yalla <i class="dot"></i> ASC Cambérène <i class="dot"></i> Plage Nord <i class="dot"></i> Diakhle <i class="dot"></i> Yoff–Cambérène <i class="dot"></i></span>
  </div>
</div>

<!-- ============================== DISCOVER (nav vers les pages) ============================== -->
<section id="discover" data-screen-label="02 Discover">
  <div class="wrap">
    <div class="section-head reveal">
      <div>
        <span class="eyebrow" data-i18n="disc_eyebrow">Sept portes vers le village</span>
        <h2 data-i18n="disc_h2">Sept entrées,<br /><span class="ital">une seule histoire.</span></h2>
      </div>
      <p class="lede" data-i18n="disc_lede">
        Choisissez votre seuil. Chaque porte ouvre sur une facette du quotidien
        cambérénois — sacrée, sportive, marchande, marine.
      </p>
    </div>

    <div class="discover-grid">
      <a class="card span-6 tall reveal" href="/histoire">
        <div class="img"><div class="ph img" data-label="Photo · Mosquée Layène · aube" style="background-image:url('https://picsum.photos/seed/cb-mosque-aube/1400/1000'); background-position:top"></div></div>
        <div class="overlay"></div>
        <div class="body">
          <span class="glass-tag">01 · Histoire</span>
          <div>
            <h3 data-i18n="disc_c1_title">Le village fondateur</h3>
            <p data-i18n="disc_c1_desc">De Seydina Limamou Laye à aujourd'hui, le récit d'une communauté assise face à l'océan.</p>
            <div class="meta">
              <span class="num" data-i18n="disc_c1_read">Lecture · 6 min</span>
              <span class="arrow-circle">↗</span>
            </div>
          </div>
        </div>
      </a>

      <a class="card span-6 tall reveal" href="/spiritualite">
        <div class="img"><div class="ph img" data-label="Photo · cérémonie blanche · procession" style="background-image:url('https://picsum.photos/seed/cb-procession/1400/1000'); background-position:center"></div></div>
        <div class="overlay"></div>
        <div class="body">
          <span class="glass-tag">02 · Culture Layène</span>
          <div>
            <h3 data-i18n="disc_c2_title">Culture Layène</h3>
            <p data-i18n="disc_c2_desc">Foi, blanc, tassou — comprendre le tariqa qui irrigue toute la vie sociale du village.</p>
            <div class="meta">
              <span class="num" data-i18n="disc_c2_read">Archive · 12 entrées</span>
              <span class="arrow-circle">↗</span>
            </div>
          </div>
        </div>
      </a>

      <a class="card span-4 reveal" href="/tourisme">
        <div class="img"><div class="ph img" data-label="Photo · plage · pirogues bleues" style="background-image:url('https://picsum.photos/seed/cb-pirogues-bleues/1400/1000'); background-position:center"></div></div>
        <div class="overlay"></div>
        <div class="body">
          <span class="glass-tag">03 · Plages</span>
          <div>
            <h3 data-i18n="disc_c3_title">Plages</h3>
            <div class="meta">
              <span class="num" data-i18n="disc_c3_count">3 spots</span>
              <span class="arrow-circle">↗</span>
            </div>
          </div>
        </div>
      </a>

      <a class="card span-4 reveal" href="/asc">
        <div class="img"><div class="ph img" data-label="Photo · terrain ASC · soir" style="background-image:url('https://picsum.photos/seed/cb-asc-soir/1400/1000'); background-position:center"></div></div>
        <div class="overlay"></div>
        <div class="body">
          <span class="glass-tag">04 · ASC &amp; Football</span>
          <div>
            <h3 data-i18n="disc_c4_title">ASC &amp; Foot</h3>
            <div class="meta">
              <span class="num" data-i18n="disc_c4_count">Saison '26</span>
              <span class="arrow-circle">↗</span>
            </div>
          </div>
        </div>
      </a>

      <a class="card span-4 reveal" href="/talents">
        <div class="img"><div class="ph img" data-label="Photo · portrait · atelier" style="background-image:url('https://picsum.photos/seed/cb-atelier/1400/1000'); background-position:center 30%"></div></div>
        <div class="overlay"></div>
        <div class="body">
          <span class="glass-tag">05 · Talents</span>
          <div>
            <h3 data-i18n="disc_c5_title">Talents</h3>
            <div class="meta">
              <span class="num" data-i18n="disc_c5_count">28 portraits</span>
              <span class="arrow-circle">↗</span>
            </div>
          </div>
        </div>
      </a>

      <a class="card span-8 wide reveal" href="/annuaire">
        <div class="img"><div class="ph img" data-label="Photo · marché du soir · lumières" style="background-image:url('https://picsum.photos/seed/cb-marche-soir/1400/1000'); background-position:center"></div></div>
        <div class="overlay"></div>
        <div class="body">
          <span class="glass-tag">06 · Business local</span>
          <div>
            <h3 data-i18n="disc_c6_title">Business local</h3>
            <p data-i18n="disc_c6_desc">Restaurants, ateliers, services — un annuaire vivant tenu par la communauté.</p>
            <div class="meta">
              <span class="num" data-i18n="disc_c6_count">142 commerces référencés</span>
              <span class="arrow-circle">↗</span>
            </div>
          </div>
        </div>
      </a>

      <a class="card span-4 reveal" href="/galerie">
        <div class="img"><div class="ph img" data-label="Photo · drone · spirale du village" style="background-image:url('https://picsum.photos/seed/cb-drone-spirale/1400/1000'); background-position:center"></div></div>
        <div class="overlay"></div>
        <div class="body">
          <span class="glass-tag">07 · Galerie</span>
          <div>
            <h3 data-i18n="disc_c7_title">Galerie</h3>
            <div class="meta">
              <span class="num" data-i18n="disc_c7_count">+ 480 clichés</span>
              <span class="arrow-circle">↗</span>
            </div>
          </div>
        </div>
      </a>
    </div>
  </div>
</section>

<!-- ============================== EXTRAS (photo du jour · le saviez-vous · météo) ============================== -->
<section class="extras" data-screen-label="09 Extras">
  <div class="wrap">
    <div class="extras-grid">
      <div class="ex-card photo-day reveal">
        <div class="ph img" data-label="Photo du jour · 09 Mai 2026" style="background-image:url('https://picsum.photos/seed/cb-photo-du-jour/1400/1000'); background-position:center"></div>
        <div class="grad"></div>
        <div class="body">
          <span class="label" data-i18n="extras_photo_label">Photo du jour · 09.05.2026</span>
          <h3 data-i18n="extras_photo_title">« Pirogues qui rentrent. »</h3>
          <p data-i18n="extras_photo_desc">Plage Nord, 18h41. Cliché par Mor Sène, photographe résident.</p>
        </div>
      </div>

      <div class="ex-card know reveal">
        <span class="label" data-i18n="extras_know_label">Le saviez-vous ?</span>
        <div class="know-num">1884</div>
        <p style="font-family:var(--serif); font-size:24px; line-height:1.15; color:var(--char-900); margin:0" data-i18n="extras_know_text">
          L'année où Cambérène est devenu un village à part entière, sous l'impulsion de Seydina Limamou Laye.
        </p>
        <p style="font-family:var(--mono); font-size:10.5px; letter-spacing:.16em; text-transform:uppercase; color:var(--char-500); margin:auto 0 0" data-i18n="extras_know_src">142 ans · Source : Archives de Cambérène</p>
      </div>

      <div class="ex-card weather reveal">
        <div class="top">
          <div>
            <span class="label" style="color:var(--gold-soft)" data-i18n="extras_weather_label">Plage de Cambérène</span>
            <div class="temp">28<span class="deg">°C</span></div>
            <div style="font-family:var(--mono); font-size:11px; letter-spacing:.18em; text-transform:uppercase; opacity:.7; margin-top:2px" data-i18n="extras_weather_desc">Ensoleillé · idéal baignade</div>
          </div>
          <div class="glyph"></div>
        </div>
        <div class="row">
          <div><b>14<span style="font-size:.5em; opacity:.6"> km/h</span></b><small data-i18n="extras_weather_w1">Vent O</small></div>
          <div><b>1.4<span style="font-size:.5em; opacity:.6"> m</span></b><small data-i18n="extras_weather_w2">Vagues</small></div>
          <div><b>18:54</b><small data-i18n="extras_weather_w3">Coucher</small></div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ============================== COUNTERS ============================== -->
<section class="counters" data-screen-label="11 Counters">
  <div class="wrap">
    <div style="display:flex; justify-content:space-between; align-items:end; margin-bottom:48px; flex-wrap:wrap; gap:16px">
      <div>
        <span class="eyebrow" style="color:rgba(250,246,238,.6)" data-i18n="counter_eyebrow">La communauté en chiffres</span>
        <h2 class="display" style="font-size:clamp(36px, 4vw, 56px); margin-top:14px; color:var(--bone)" data-i18n="counter_h2">Cambérène, vivante.</h2>
      </div>
      <div style="font-family:var(--mono); font-size:11px; letter-spacing:.18em; text-transform:uppercase; color:rgba(250,246,238,.5)" data-i18n="counter_updated">↻ Mis à jour il y a 2 minutes</div>
    </div>

    <div class="counters-grid">
      <div class="counter"><b data-target="38400">0</b><span data-i18n="counter_c1">Habitants</span></div>
      <div class="counter"><b data-target="142">0</b><span data-i18n="counter_c2">Commerces</span></div>
      <div class="counter"><b data-target="480"><span class="unit">+</span></b><span data-i18n="counter_c3">Photos partagées</span></div>
      <div class="counter"><b data-target="28">0</b><span data-i18n="counter_c4">Talents portés</span></div>
    </div>
  </div>
</section>

<!-- ============================== MOBILE PREVIEW ============================== -->
<section class="mobile-section" data-screen-label="12 Mobile">
  <div class="wrap">
    <div class="section-head reveal">
      <div>
        <span class="eyebrow" data-i18n="mobile_eyebrow">Version mobile · iOS / Android</span>
        <h2 data-i18n="mobile_h2">Toujours dans<br /><span class="ital">votre poche.</span></h2>
      </div>
      <p class="lede" data-i18n="mobile_lede">L'expérience Cambérène pensée verticalement — feeds, profils, alertes événements et matchs en direct.</p>
    </div>

    <div class="phones">
      <div>
        <div class="phone reveal">
          <div class="screen">
            <div class="notch"></div>
            <div class="ms-status"><span>09:41</span><span>● ◐ ▮▮</span></div>
            <div class="ms-hero">
              <div class="ph img" data-label="" style="background-image:url('https://picsum.photos/seed/cb-mob-hero/900/900'); background-position:center"></div>
              <div class="grad"></div>
              <div class="ms-nav">
                <span class="b">Cambérène</span>
                <span class="menu"><span></span></span>
              </div>
              <div class="body">
                <h2>Entre rives <span class="ital">&amp; lumière.</span></h2>
                <div class="row">
                  <span class="b1">Découvrir →</span>
                  <span class="b2">Culture</span>
                </div>
              </div>
            </div>
            <div class="ms-bottom">
              <h4 class="h" data-i18n="mobile_screen1_explore">À explorer</h4>
              <div class="ms-tiles">
                <div class="ms-tile"><div class="ph img" data-label="" style="background-image:url('https://picsum.photos/seed/cb-mob-tile1/900/900'); background-position:center"></div><span>Plages</span></div>
                <div class="ms-tile"><div class="ph img" data-label="" style="background-image:url('https://picsum.photos/seed/cb-mob-tile2/900/900'); background-position:center"></div><span>Layène</span></div>
              </div>
            </div>
          </div>
        </div>
        <div class="phone-cap" data-i18n="mobile_cap1">01 · Accueil</div>
      </div>

      <div>
        <div class="phone reveal">
          <div class="screen">
            <div class="notch"></div>
            <div class="ms-status"><span>09:41</span><span>● ◐ ▮▮</span></div>
            <div class="ms-feed">
              <div class="ms-search"><span class="ic"></span> <span data-i18n="mobile_screen2_search">Rechercher un lieu, un talent…</span></div>
              <span class="eyebrow" data-i18n="mobile_screen2_today">Aujourd'hui · 9 mai</span>
              <h3 data-i18n="mobile_screen2_journal">Le journal du village.</h3>
              <article class="ms-art">
                <div class="img"><div class="ph img" data-label="" style="background-image:url('https://picsum.photos/seed/cb-mob-art1/900/900'); background-position:center"></div></div>
                <span class="cat" data-i18n="mobile_cat1">Jeunesse</span>
                <p class="ti" data-i18n="mobile_art1">Vingt jeunes lancent leur studio créatif.</p>
              </article>
              <article class="ms-art">
                <div class="img"><div class="ph img" data-label="" style="background-image:url('https://picsum.photos/seed/cb-mob-art2/900/900'); background-position:center"></div></div>
                <span class="cat" data-i18n="mobile_cat2">Plage</span>
                <p class="ti" data-i18n="mobile_art2">380 kg de déchets retirés en un samedi.</p>
              </article>
            </div>
            <div class="ms-tab">
              <span class="on" data-i18n="mobile_tab1">● Accueil</span><span data-i18n="mobile_tab2">Carte</span><span data-i18n="mobile_tab3">News</span><span data-i18n="mobile_tab4">Profil</span>
            </div>
          </div>
        </div>
        <div class="phone-cap" data-i18n="mobile_cap2">02 · Feed</div>
      </div>

      <div>
        <div class="phone reveal">
          <div class="screen">
            <div class="notch"></div>
            <div class="ms-status"><span>09:41</span><span>● ◐ ▮▮</span></div>
            <div class="ms-prof">
              <div class="cover"><div class="ph img" data-label="" style="background-image:url('https://picsum.photos/seed/cb-mob-cover/900/900'); background-position:center"></div></div>
              <div class="av"><div class="ph img" data-label="" style="background-image:url('https://picsum.photos/seed/cb-mob-avatar/900/900'); background-position:center"></div></div>
              <div class="body">
                <h3>Aïssatou N'Diaye</h3>
                <div class="role" data-i18n="mobile_profile_role">Voix Tassou · Musique</div>
                <p data-i18n="mobile_profile_desc">Mêle chants layènes traditionnels et productions électroniques contemporaines.</p>
                <div class="stats">
                  <div><b>12k</b><small>Suivis</small></div>
                  <div><b>34</b><small>Sets</small></div>
                  <div><b>2026</b><small>Album</small></div>
                </div>
              </div>
            </div>
            <div class="ms-tab" style="background:var(--sunset)">
              <span style="color:#fff" data-i18n="mobile_follow">Suivre +</span><span style="color:rgba(255,255,255,.7)" data-i18n="mobile_listen">Écouter</span><span style="color:rgba(255,255,255,.7)" data-i18n="mobile_share">Partager</span>
            </div>
          </div>
        </div>
        <div class="phone-cap" data-i18n="mobile_cap3">03 · Talent</div>
      </div>
    </div>
  </div>
</section>

<!-- ============================== NEWSLETTER ============================== -->
<section class="newsletter" data-screen-label="13 Newsletter">
  <div class="wrap">
    <div class="nl-grid">
      <div>
        <span class="eyebrow" style="color:var(--gold-soft)" data-i18n="nl_eyebrow">Lettre du village · Mensuelle</span>
        <h2 data-i18n="nl_h2">Recevez Cambérène<br /><span class="ital">dans votre boîte.</span></h2>
      </div>
      <div>
        <p class="lede" style="color:rgba(250,246,238,.78)" data-i18n="nl_lede">Chaque premier dimanche : un récit, une photo du jour, les matchs de l'ASC, les nouveaux portraits, et l'agenda culturel du mois.</p>
        <form class="nl-form" onsubmit="event.preventDefault(); this.querySelector('button').textContent=(window.currentLang==='fr'?'Merci !':'Thank you!');">
          <input type="email" placeholder="votre@adresse.email" data-i18n-ph="nl_email_placeholder" />
          <button type="submit" data-i18n="nl_btn">S'abonner</button>
        </form>
        <div class="nl-fine" data-i18n="nl_fine">3 200 abonnés · sans spam · désabonnement en un clic</div>
      </div>
    </div>
  </div>
</section>

<!-- ============================== LIVE BANNER ============================== -->
<div class="live-banner" id="live-banner">
  <span class="live-dot"></span>
  <span class="tag" data-i18n="live_tag">EN DIRECT</span>
  <span data-i18n="live_score"><b>ASC Cambérène 2 — 1 ASC Yoff</b> · 78ᵉ minute</span>
  <span class="sep"></span>
  <span style="opacity:.7" data-i18n="live_venue">Stade Iba Mar Diop</span>
  <button class="close" aria-label="Fermer" onclick="document.getElementById('live-banner').style.display='none'">✕</button>
</div>
`;

const Accueil = () => {
  useEffect(() => {
    window.toggleLang = toggleLang;
    window.currentLang = getCurrentLang();

    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    document.querySelectorAll('.reveal').forEach(el => io.observe(el));

    /* ===== Compteurs LIVE : on remplace les valeurs hardcodées par les vrais totaux ===== */
    let cancelled = false;
    (async () => {
      const [commerces, photos, talents, articles, campagnes, sites] = await Promise.all([
        fetchCount('/commerces'),
        fetchCount('/photos'),
        fetchCount('/talents'),
        fetchCount('/articles'),
        fetchCount('/caisse/campagnes'),
        fetchCount('/tourisme/sites'),
      ]);
      if (cancelled) return;

      // Mapping : 4 compteurs principaux + bandeau de mise à jour
      // 1ᵉʳ counter = Habitants (hardcodé, pas d'API publique)
      const updateTarget = (selector, value) => {
        if (value === null || value === undefined) return;
        const el = document.querySelector(selector);
        if (!el) return;
        el.dataset.target = String(value);
        // Si l'animation a déjà eu lieu (textContent non vide / non zéro), on rafraîchit
        const cur = parseInt((el.textContent || '0').replace(/\D/g, ''), 10) || 0;
        if (cur > 0 && cur !== value) {
          const unit = el.querySelector('.unit');
          el.textContent = value.toLocaleString('fr-FR');
          if (unit) el.appendChild(unit);
        }
      };
      // Counter 2 : Commerces · Counter 3 : Photos · Counter 4 : Talents
      const counters = document.querySelectorAll('.counter b[data-target]');
      if (counters[1]) updateTarget('.counter:nth-child(2) b', commerces);
      if (counters[2]) updateTarget('.counter:nth-child(3) b', photos);
      if (counters[3]) updateTarget('.counter:nth-child(4) b', talents);

      // Bandeau "Mis à jour"
      const stamp = document.querySelector('[data-i18n="counter_updated"]');
      if (stamp) {
        const t = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        stamp.textContent = `↻ Mis à jour à ${t}`;
      }

      // Si on veut afficher d'autres totaux ailleurs (articles/campagnes/sites),
      // on expose ces compteurs globalement pour pouvoir y accéder côté DOM si besoin.
      window.__camStats = { commerces, photos, talents, articles, campagnes, sites };
    })();

    const counterIO = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        const el = e.target;
        const target = parseInt(el.dataset.target, 10);
        if (isNaN(target)) return;
        const duration = 1600;
        const start = performance.now();
        const fmt = n => n.toLocaleString('fr-FR');
        const tick = (now) => {
          const t = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - t, 3);
          const v = Math.floor(target * eased);
          const unit = el.querySelector('.unit');
          el.textContent = fmt(v);
          if (unit) el.appendChild(unit);
          if (t < 1) requestAnimationFrame(tick);
          else { el.textContent = fmt(target); if (unit) el.appendChild(unit); }
        };
        requestAnimationFrame(tick);
        counterIO.unobserve(el);
      });
    }, { threshold: 0.4 });
    document.querySelectorAll('.counter b[data-target]').forEach(el => counterIO.observe(el));

    // Smooth scroll uniquement pour les ancres internes restantes (#discover)
    document.querySelectorAll('a[href^="#"]').forEach(a => {
      a.addEventListener('click', e => {
        const id = a.getAttribute('href');
        if (id.length < 2) return;
        const t = document.querySelector(id);
        if (!t) return;
        e.preventDefault();
        window.scrollTo({ top: t.offsetTop - 60, behavior: 'smooth' });
      });
    });

    applyLang(getCurrentLang());

    return () => {
      cancelled = true;
      io.disconnect();
      counterIO.disconnect();
      delete window.toggleLang;
      delete window.currentLang;
    };
  }, []);

  return (
    <div
      style={{ margin: 0, padding: 0 }}
      dangerouslySetInnerHTML={{ __html: BODY_HTML }}
    />
  );
};

export default Accueil;
